import React, { Fragment, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Badge, Input, Label, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter, Button, Table } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import DataTable from 'react-data-table-component';
import { useGetMovementLogQuery, useGetStoresQuery } from '../../../../store/services/inventoryApi';
import { Eye, Activity } from 'react-feather';
import moment from 'moment';

const MovementLog = () => {
    const [storeFilter, setStoreFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [selectedMovement, setSelectedMovement] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { data: movementsData, isLoading } = useGetMovementLogQuery({
        storeId: storeFilter,
        entityType: typeFilter
    });

    const { data: storesData } = useGetStoresQuery();

    const movements = useMemo(() => {
        if (!movementsData) return [];
        return Array.isArray(movementsData) ? movementsData : (movementsData.data || []);
    }, [movementsData]);

    const toggleModal = () => setIsModalOpen(!isModalOpen);

    const viewDetails = (row) => {
        setSelectedMovement(row);
        toggleModal();
    };

    const columns = [
        {
            name: 'Date & Time',
            selector: row => moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss'),
            sortable: true,
            width: '180px'
        },
        {
            name: 'Product / PO',
            selector: row => row.purchaseOrder?.poNumber || row.transferInfo?.transferNumber || row.returnSupplier?.returnNumber || row.stocktake?.stocktakeNumber || row.productVariantId?.productName || 'N/A',
            sortable: true,
            cell: row => {
                if (row.purchaseOrder) {
                    return (
                        <div className="py-2">
                            <div className="fw-bold text-primary">{row.purchaseOrder.poNumber}</div>
                            <small className="text-muted">{row.purchaseOrder.supplier} | {row.purchaseOrder.itemCount} item(s) | <span className={`fw-bold text-${row.purchaseOrder.status === 'Closed' ? 'success' : row.purchaseOrder.status === 'Cancelled' ? 'danger' : 'warning'}`}>{row.purchaseOrder.status}</span></small>
                        </div>
                    );
                }
                if (row.transferInfo) {
                    return (
                        <div className="py-2">
                            <div className="fw-bold text-primary">{row.transferInfo.transferNumber}</div>
                            <small className="text-muted">Transfer: {row.transferInfo.fromStore} → {row.transferInfo.toStore} | <span className={`fw-bold text-${row.transferInfo.status === 'received' ? 'success' : row.transferInfo.status === 'cancelled' ? 'danger' : 'warning'}`}>{row.transferInfo.status}</span></small>
                        </div>
                    );
                }
                if (row.returnSupplier) {
                    return (
                        <div className="py-2">
                            <div className="fw-bold text-primary">{row.returnSupplier.returnNumber}</div>
                            <small className="text-muted">Return: {row.returnSupplier.supplier} | {row.returnSupplier.itemCount} item(s) | <span className={`fw-bold text-${row.returnSupplier.status === 'Completed' ? 'success' : row.returnSupplier.status === 'Cancelled' ? 'danger' : 'warning'}`}>{row.returnSupplier.status}</span></small>
                        </div>
                    );
                }
                if (row.stocktake) {
                    return (
                        <div className="py-2">
                            <div className="fw-bold text-primary">{row.stocktake.stocktakeNumber}</div>
                            <small className="text-muted">{row.stocktake.type} Stocktake | {row.stocktake.itemCount} item(s) | <span className={`fw-bold text-${row.stocktake.status === 'Completed' ? 'success' : row.stocktake.status === 'Cancelled' ? 'danger' : 'warning'}`}>{row.stocktake.status}</span></small>
                        </div>
                    );
                }
                return (
                    <div className="py-2">
                        <div className="fw-bold text-primary">{row.productVariantId?.productName || 'N/A'}</div>
                        <small className="text-muted">{row.productVariantId?.variantName} | SKU: <span className="fw-bold">{row.productVariantId?.sku || 'N/A'}</span></small>
                    </div>
                );
            },
            width: '320px'
        },
        {
            name: 'Store',
            selector: row => row.storeId?.storeName || 'N/A',
            sortable: true,
        },
        {
            name: 'Action',
            selector: row => row.action,
            sortable: true,
            cell: row => {
                let color = 'secondary';
                if (row.action.includes('RECEIVE')) color = 'success';
                if (row.action.includes('SHIP') || row.action.includes('DISPATCH')) color = 'warning';
                if (row.action.includes('ADJUST')) color = 'info';
                if (row.action.includes('STOCKTAKE')) color = 'primary';
                return <Badge color={color} pill>{row.action}</Badge>;
            }
        },
        {
            name: 'Change',
            selector: row => row.quantityChange,
            sortable: true,
            cell: row => row.purchaseOrder ? (
                <span className="fw-bold text-info">{row.quantityChange} units ordered</span>
            ) : (
                <span className={`fw-bold ${row.quantityChange > 0 ? 'text-success' : 'text-danger'}`}>
                    {row.quantityChange > 0 ? `+${row.quantityChange}` : row.quantityChange}
                </span>
            ),
            width: '160px'
        },
        {
            name: 'Actions',
            cell: row => (
                <Button color="light" size="xs" onClick={() => viewDetails(row)} className="px-2">
                    <Eye size={14} className="text-primary" />
                </Button>
            ),
            width: '80px',
            center: true
        }
    ];

    return (
        <Fragment>
            <Breadcrumbs mainTitle="Inventory Management" parent="Inventory" title="Audit Trail" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card className="shadow-sm">
                            <CardHeader className="bg-white border-bottom py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="mb-1"><Activity size={20} className="text-secondary me-2" /> Stock Movement Audit Trail</h5>
                                        <p className="text-muted mb-0 small">Immutable records of all transactions affecting store stock levels.</p>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <div className="mb-4 bg-light p-3 rounded-3 d-flex gap-3 align-items-end flex-wrap border">
                                    <FormGroup className="mb-0">
                                        <Label className="small fw-bold text-muted mb-1">STORE CONTEXT</Label>
                                        <Input type="select" size="sm" value={storeFilter} onChange={e => setStoreFilter(e.target.value)} className="border-secondary-subtle">
                                            <option value="">All Stores</option>
                                            {storesData?.data?.map(w => (
                                                <option key={w._id} value={w._id}>{w.storeName} {w.storeNumber ? `(${w.storeNumber})` : ''}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                    <FormGroup className="mb-0">
                                        <Label className="small fw-bold text-muted mb-1">TRANSACTION CATEGORY</Label>
                                        <Input type="select" size="sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="border-secondary-subtle">
                                            <option value="">All Transactions</option>
                                            <option value="PURCHASE_ORDER">Purchase Orders</option>
                                            <option value="TRANSFER">Internal Transfers</option>
                                            <option value="RETURN_TO_SUPPLIER">Returns to Supplier</option>
                                            <option value="STOCK_ADJUSTMENT">Manual Stock Adjustments</option>
                                            <option value="STOCKTAKE">Stocktake Sessions</option>
                                        </Input>
                                    </FormGroup>
                                </div>

                                <DataTable
                                    columns={columns}
                                    data={movements}
                                    pagination
                                    progressPending={isLoading}
                                    highlightOnHover
                                    striped
                                    responsive
                                    noDataComponent={<div className="p-5 text-center text-muted">No movement records found for the selected filters.</div>}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Drill-down Detail Modal */}
            <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg" centered>
                <ModalHeader toggle={toggleModal} className="bg-light">
                    <Eye size={18} className="me-2 text-primary" /> Transaction Details
                </ModalHeader>
                <ModalBody>
                    {selectedMovement && (
                        <div className="p-2">
                            <Row className="mb-4">
                                <Col md="6">
                                    {selectedMovement.purchaseOrder || selectedMovement.transferInfo || selectedMovement.returnSupplier || selectedMovement.stocktake ? (
                                        <>
                                            <Label className="text-muted small fw-bold">TRANSACTION INFO</Label>
                                            <h5 className="text-primary">
                                                {selectedMovement.purchaseOrder?.poNumber || 
                                                 selectedMovement.transferInfo?.transferNumber || 
                                                 selectedMovement.returnSupplier?.returnNumber || 
                                                 selectedMovement.stocktake?.stocktakeNumber}
                                            </h5>
                                            <p className="mb-0">
                                                {selectedMovement.purchaseOrder ? `Supplier: ${selectedMovement.purchaseOrder.supplier}` :
                                                 selectedMovement.transferInfo ? `Store: ${selectedMovement.transferInfo.fromStore} → ${selectedMovement.transferInfo.toStore}` :
                                                 selectedMovement.returnSupplier ? `Supplier: ${selectedMovement.returnSupplier.supplier}` :
                                                 selectedMovement.stocktake ? `${selectedMovement.stocktake.type} Stocktake` : ''}
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <Label className="text-muted small fw-bold">PRODUCT INFO</Label>
                                            <h5 className="text-primary">{selectedMovement.productVariantId?.productName || 'Unknown Product'}</h5>
                                            <p className="mb-0">{selectedMovement.productVariantId?.variantName}</p>
                                            <small className="text-muted">SKU: {selectedMovement.productVariantId?.sku || 'N/A'}</small>
                                        </>
                                    )}
                                </Col>
                                <Col md="6" className="text-md-end">
                                    <Label className="text-muted small fw-bold">TIMELINE</Label>
                                    <div>{moment(selectedMovement.createdAt).format('LLLL')}</div>
                                    <Badge color="secondary" pill className="mt-2">{selectedMovement.action}</Badge>
                                </Col>
                            </Row>

                            <Table bordered size="sm" className="mb-4">
                                <thead className="table-light">
                                    <tr>
                                        <th>Attribute</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Store</td>
                                        <td>{selectedMovement.storeId?.storeName || 'N/A'}</td>
                                    </tr>
                                    {selectedMovement.purchaseOrder ? (
                                        <>
                                            <tr><td>PO Number</td><td className="fw-bold text-primary">{selectedMovement.purchaseOrder.poNumber}</td></tr>
                                            <tr><td>Status</td><td><Badge color={selectedMovement.purchaseOrder.status === 'Closed' ? 'success' : selectedMovement.purchaseOrder.status === 'Cancelled' ? 'danger' : 'warning'}>{selectedMovement.purchaseOrder.status}</Badge></td></tr>
                                            <tr><td>Supplier</td><td>{selectedMovement.purchaseOrder.supplier}{selectedMovement.purchaseOrder.supplierEmail ? ` (${selectedMovement.purchaseOrder.supplierEmail})` : ''}</td></tr>
                                            <tr><td>Expected Date</td><td>{selectedMovement.purchaseOrder.expectedDate ? moment(selectedMovement.purchaseOrder.expectedDate).format('DD MMM YYYY') : 'N/A'}</td></tr>
                                            <tr><td>Total Value</td><td className="fw-bold">{selectedMovement.purchaseOrder.currency} {selectedMovement.purchaseOrder.totalAmount?.toFixed(2)}</td></tr>
                                            <tr><td>Total Units Ordered</td><td className="fw-bold text-info">{selectedMovement.quantityChange} units across {selectedMovement.purchaseOrder.itemCount} line(s)</td></tr>
                                            <tr>
                                                <td colSpan="2">
                                                    <strong className="small text-muted d-block mb-2">LINE ITEMS</strong>
                                                    <Table size="sm" bordered className="mb-0">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th>Product</th>
                                                                <th>SKU</th>
                                                                <th>Ordered</th>
                                                                <th>Received</th>
                                                                <th>Unit Cost</th>
                                                                <th>Received Cost</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedMovement.purchaseOrder.items.map((item, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{item.productName}</td>
                                                                    <td><code>{item.sku || 'N/A'}</code></td>
                                                                    <td className="fw-bold">{item.orderedQty}</td>
                                                                    <td>{item.receivedQty ?? 0}</td>
                                                                    <td>{selectedMovement.purchaseOrder.currency} {item.unitCost?.toFixed(2)}</td>
                                                                    <td>
                                                                        {item.receivedUnitCost !== undefined && item.receivedUnitCost !== null ? (
                                                                            `${selectedMovement.purchaseOrder.currency} ${item.receivedUnitCost.toFixed(2)}`
                                                                        ) : (
                                                                            <span className="text-muted">—</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </td>
                                            </tr>
                                        </>
                                    ) : selectedMovement.transferInfo ? (
                                        <>
                                            <tr><td>Transfer Number</td><td className="fw-bold text-primary">{selectedMovement.transferInfo.transferNumber}</td></tr>
                                            <tr><td>Status</td><td><Badge color={selectedMovement.transferInfo.status === 'received' ? 'success' : selectedMovement.transferInfo.status === 'cancelled' ? 'danger' : 'warning'}>{selectedMovement.transferInfo.status}</Badge></td></tr>
                                            <tr><td>From Store</td><td>{selectedMovement.transferInfo.fromStore}</td></tr>
                                            <tr><td>To Store</td><td>{selectedMovement.transferInfo.toStore}</td></tr>
                                            <tr><td>Reason/Note</td><td>{selectedMovement.reason || 'N/A'}</td></tr>
                                        </>
                                    ) : selectedMovement.returnSupplier ? (
                                        <>
                                            <tr><td>Return Number</td><td className="fw-bold text-primary">{selectedMovement.returnSupplier.returnNumber}</td></tr>
                                            <tr><td>Status</td><td><Badge color={selectedMovement.returnSupplier.status === 'Completed' ? 'success' : selectedMovement.returnSupplier.status === 'Cancelled' ? 'danger' : 'warning'}>{selectedMovement.returnSupplier.status}</Badge></td></tr>
                                            <tr><td>Supplier</td><td>{selectedMovement.returnSupplier.supplier}{selectedMovement.returnSupplier.supplierEmail ? ` (${selectedMovement.returnSupplier.supplierEmail})` : ''}</td></tr>
                                            <tr><td>Dispatched Date</td><td>{selectedMovement.returnSupplier.dispatchedDate ? moment(selectedMovement.returnSupplier.dispatchedDate).format('DD MMM YYYY') : 'N/A'}</td></tr>
                                            <tr><td>Total Units Returned</td><td className="fw-bold text-danger">{Math.abs(selectedMovement.quantityChange)} units</td></tr>
                                            <tr><td>Reason/Note</td><td>{selectedMovement.reason || 'N/A'}</td></tr>
                                        </>
                                    ) : selectedMovement.stocktake ? (
                                        <>
                                            <tr><td>Stocktake Number</td><td className="fw-bold text-primary">{selectedMovement.stocktake.stocktakeNumber}</td></tr>
                                            <tr><td>Type</td><td>{selectedMovement.stocktake.type}</td></tr>
                                            <tr><td>Status</td><td><Badge color={selectedMovement.stocktake.status === 'Completed' ? 'success' : selectedMovement.stocktake.status === 'Cancelled' ? 'danger' : 'warning'}>{selectedMovement.stocktake.status}</Badge></td></tr>
                                            <tr><td>Completed Date</td><td>{selectedMovement.stocktake.completedDate ? moment(selectedMovement.stocktake.completedDate).format('DD MMM YYYY') : 'N/A'}</td></tr>
                                            <tr><td>Items Checked</td><td>{selectedMovement.stocktake.itemCount} line(s)</td></tr>
                                            <tr>
                                                <td>Net Variance</td>
                                                <td className={selectedMovement.quantityChange > 0 ? 'text-success fw-bold' : selectedMovement.quantityChange < 0 ? 'text-danger fw-bold' : ''}>
                                                    {selectedMovement.quantityChange > 0 ? `+${selectedMovement.quantityChange}` : selectedMovement.quantityChange} units
                                                </td>
                                            </tr>
                                            <tr><td>Reason/Note</td><td>{selectedMovement.reason || 'N/A'}</td></tr>
                                        </>
                                    ) : (
                                        <>
                                            <tr>
                                                <td>Quantity Change</td>
                                                <td className={selectedMovement.quantityChange > 0 ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                                                    {selectedMovement.quantityChange > 0 ? `+${selectedMovement.quantityChange}` : selectedMovement.quantityChange} units
                                                </td>
                                            </tr>
                                            <tr><td>Reason/Note</td><td>{selectedMovement.reason || 'N/A'}</td></tr>
                                        </>
                                    )}
                                    <tr>
                                        <td>Performed By</td>
                                        <td>{selectedMovement.performedBy?.email || 'System'}</td>
                                    </tr>
                                </tbody>
                            </Table>

                            <Label className="text-muted small fw-bold mt-2">TECHNICAL METADATA (JSON)</Label>
                            <pre className="bg-dark text-light p-3 rounded" style={{ fontSize: '12px', maxHeight: '200px', overflowY: 'auto' }}>
                                {JSON.stringify(selectedMovement, null, 2)}
                            </pre>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>Close</Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default MovementLog;
